import Wrapper from 'components/wrapper';
import { fetchData } from 'lib/fetchData';
import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import * as React from 'react';
import { API } from '_helpers';

const Header = dynamic(import('components/header'));
const TrainingLayout = dynamic(
  import('components/ourTrainings/components/trainingLayout')
);
const TrainingList = dynamic(
  import('components/ourTrainings/components/trainingList')
);

interface IProps {
  header: IHeaders;
  menu: any;
  footer: any;
  company: any;
  courses: any;
  sideBar: any;
  categories: any;
  tags: any;
  page: number;
  count: number;
  seo: any;
  locale: any;
  resolvedUrl: any;
}

const OurTrainings: NextPage<IProps> = (props) => {
  const {
    menu,
    header,
    footer,
    company,
    courses,
    page,
    count,
    seo,
    locale,
    resolvedUrl,
  } = props;
  return (
    <Wrapper menu={menu} company={company} footer={footer}>
      <Head>
        <title>{seo.pages[7].title}</title>
        <meta name="description" content={seo.pages[7].description} />
        <link rel="icon" href={API + company.favicon.media.url} />
        <meta name="robots" content="index,follow" />
        <meta name="twitter:dnt" content="on" />
        <meta
          property="article:published_time"
          content={new Date().toLocaleTimeString()}
        />
        <meta
          name="publish_date"
          property="og:publish_date"
          content={new Date().toLocaleDateString()}
        />
        <meta
          name="author"
          property="article:author"
          content="Luccin Masirika"
        />
        {/* Open Graph */}
        <meta property="og:title" content={seo.pages[7].title} />
        <meta property="og:description" content={seo.pages[7].description} />
        <meta
          property="og:url"
          content={`https://uptodatedevelopers.com${resolvedUrl}`}
        />
        <meta property="og:image" content={API + seo.pages[7].media.url} />
        <meta property="og:site_name" content={company.name} />
        <meta property="og:locale" content={locale} />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={company.name} />
      </Head>
      <Header data={header.trainings} />
      <TrainingLayout>
        <TrainingList data={courses} page={page} count={count} />
      </TrainingLayout>
    </Wrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  query: { page = 1 },
  resolvedUrl,
}) => {
  const start: number = +page === 1 ? 0 : (+page - 1) * 6;

  const count = await fetchData(`/courses/count?_locale=${locale}`);

  const pages = Math.ceil(count / 6);

  const courses = await fetchData(
    `/courses/?_locale=${locale}&_limit=6&_start=${start}`
  );
  const menu = await fetchData(`/menu/?_locale=${locale}`);
  const company = await fetchData(`/company/?_locale=${locale}`);
  const footer = await fetchData(`/footer/?_locale=${locale}`);
  const header = await fetchData(`/headers/?_locale=${locale}`);
  const seo = await fetchData(`/seo/?_locale=${locale}`);

  if (courses.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      courses,
      menu,
      company,
      footer,
      header,
      count: pages,
      page: +page,
      seo,
      locale,
      resolvedUrl,
    },
  };
};

export default OurTrainings;
